import {AfterViewInit, Component, ElementRef, Injector, Input, OnInit, ViewChild} from '@angular/core'
import {ControlValueAccessorConnectorDirective} from '../control-value-accessor-connector.directive'
import {AbstractControl, UntypedFormControl, NG_VALUE_ACCESSOR, Validators} from '@angular/forms'
import {faMinus} from '@fortawesome/pro-regular-svg-icons/faMinus'
import {faPlus} from '@fortawesome/pro-regular-svg-icons/faPlus'
import {isControlInvalid} from '../form.types'

@Component({
  selector: 'cft-number-input',
  templateUrl: './number-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: NumberInputComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class NumberInputComponent extends ControlValueAccessorConnectorDirective implements OnInit, AfterViewInit {
  @Input() min!: number
  @Input() max!: number
  @Input() step = 1
  @Input() label?: string
  @Input() hasWarning = false
  @Input() defaultDisplayValue? = 0

  readonly plus = faPlus
  readonly minus = faMinus
  private readonly _id = this.elementRef.nativeElement.getAttribute('id')
  @ViewChild('inputElement') private readonly inputElement!: ElementRef<HTMLInputElement>

  constructor(
    injector: Injector,
    private readonly elementRef: ElementRef,
  ) {
    super(injector)
  }

  get id(): string | undefined {
    return this._id ?? this.controlName
  }

  /*
   * todo Remove this logic from the component.
   * Validators should ONLY be set where the FormControl is defined, and not on the component itself. Because multiple
   * instances of this component could potentially modify the FormControl E.g. when you have this component inside an *ngIf,
   * and the condition is triggered multiple times (will create/destroy this component multiple times)
   */
  ngOnInit(): void {
    let isRequired = false
    if (this.control?.validator) {
      isRequired = this.control?.validator(new UntypedFormControl())?.required ?? false
    }

    this.control?.clearValidators()
    this.control?.setValidators([
      //use arrow functions here because this.min & this.max could change and needs to be evaluated on every validation
      (control: AbstractControl) => Validators.min(this.min)(control),
      (control: AbstractControl) => Validators.max(this.max)(control),
      isRequired ? Validators.required : Validators.nullValidator,
    ])
  }

  ngAfterViewInit(): void {
    //The default display value shouldn't be set at the FormControl level. It's only for display purposes i.e.
    //the actual value of the control remains undefined
    if (!this.control?.value && this.defaultDisplayValue !== undefined)
      this.inputElement.nativeElement.valueAsNumber = this.defaultDisplayValue
  }

  increment(): void {
    const currentValue = this.control?.value ?? 0
    if (currentValue + this.step > this.max) {
      this.control?.setValue(this.max)
      return
    }

    if (currentValue < this.min) {
      this.control?.setValue(this.min)
      return
    }

    this.control?.setValue(currentValue + this.step)
  }

  decrement(): void {
    const currentValue = this.control?.value ?? 0
    if (currentValue - this.step < this.min) {
      this.control?.setValue(this.min)
      return
    }

    if (currentValue > this.max) {
      this.control?.setValue(this.max)
      return
    }

    this.control?.setValue(currentValue - this.step)
  }

  protected readonly isControlInvalid = isControlInvalid
}
